import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { feedbackAPI } from '../services/api';
import { useSnackbar } from '../components/Snackbar';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import './Feedback.css';

const PAGE_SIZE = 10;

const fileIcon = (type) => {
  if (type?.includes('pdf')) return '📄';
  if (type?.includes('word') || type?.includes('document')) return '📝';
  if (type?.startsWith('image/')) return '📸';
  return '📎';
};

const STATUS_LABEL = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  need_info: 'Need Info',
  wont_fix: "Won't Fix",
};

const Feedback = () => {
  const snack = useSnackbar();
  const [history,    setHistory]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [deletingKey, setDeletingKey] = useState(null); 
  const [page,        setPage]        = useState(1);

  useEffect(() => {
    feedbackAPI.getAll()
      .then(r => setHistory(Array.isArray(r.data) ? r.data : []))
      .catch(err => {
        console.error('[Feedback] failed to load:', err);
        // Only surface the error toast for a genuine request failure (network/auth/server).
        // A successful response with no rows should just fall through to the empty state below.
        if (err?.response || err?.request) snack('Failed to load feedback.', 'error');
        setHistory([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = useCallback(async (entry) => {
    const key = `${entry.report_id}-${entry.created_at}`;
    setDeletingKey(key);
    try {
      await feedbackAPI.remove(entry.report_id, entry.created_at);
      setHistory(h => {
        const next = h.filter(f => !(f.report_id === entry.report_id && f.created_at === entry.created_at));
        const newTotalPages = Math.max(1, Math.ceil(next.length / PAGE_SIZE));
        setPage(p => Math.min(p, newTotalPages));
        return next;
      });
      snack('Feedback deleted.', 'success');
    } catch {
      snack('Failed to delete feedback.', 'error');
    } finally {
      setDeletingKey(null);
    }
  }, [snack]);

  const fmt = iso => new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const totalPages = Math.max(1, Math.ceil(history.length / PAGE_SIZE));
  const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce((acc, p, idx, arr) => {
      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
      acc.push(p);
      return acc;
    }, []);
  const goToPage = (p) => setPage(p);
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageItems = history.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="feedback-page">
      <div className="fb-page-header">
        <h1 className="page-title" style={{ marginBottom: 0 }}>My Feedback</h1>
      </div>
      <p className="fb-page-sub">
        Feedback you've submitted across all of your reports. To leave new feedback, open a report and use its Feedback tab.
      </p>

      <div className="table-card fb-table-card">
        {loading ? (
          <div className="fb-loading-wrap">
            <div className="fb-loading-dot" /><div className="fb-loading-dot" /><div className="fb-loading-dot" />
          </div>
        ) : history.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <div className="empty-title">No feedback yet</div>
            <div className="empty-sub">Feedback you submit from a report's Feedback tab will show up here.</div>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="reports-table fb-table">
              <colgroup><col /><col /><col /><col /><col /><col /><col /></colgroup>
              <thead>
                <tr><th>Date</th><th>Report</th><th>Technique</th><th>Status</th><th>Message</th><th>Attachments</th><th></th></tr>
              </thead>
              <tbody>
                {pageItems.map(fb => {
                  const key = `${fb.report_id}-${fb.created_at}`;
                  const status = fb.status || 'open';
                  return (
                    <tr key={key}>
                      <td className="td-date">{fmt(fb.created_at)}</td>
                      <td>
                        {fb.report_modality
                          ? <span className="modality-pill">{fb.report_modality}</span>
                          : <span className="na">—</span>}
                        {fb.report_body_part && <span className="fb-report-bp"> · {fb.report_body_part}</span>}
                      </td>
                      <td className="td-body">{fb.report_technique || <span className="na">—</span>}</td>
                      <td>
                        <span className={`fb-status-pill fb-status-${status}`} title={fb.admin_note || ''}>
                          {STATUS_LABEL[status] || status}
                        </span>
                        {fb.admin_note && <div className="fb-admin-note">{fb.admin_note}</div>}
                      </td>
                      <td className="fb-msg-cell">{fb.message}</td>
                      <td>
                        {fb.files?.length > 0 ? (
                          <div className="fb-hi-files">
                            {fb.files.map((f, i) => (
                              <a key={i} href={f.url} target="_blank" rel="noreferrer" className="fb-hi-file">
                                {fileIcon(f.type)} <span>{f.name.length > 16 ? f.name.slice(0, 14) + '…' : f.name}</span>
                              </a>
                            ))}
                          </div>
                        ) : <span className="na">—</span>}
                      </td>
                      <td className="fb-actions-cell">
                        <Link to={`/reports/${fb.report_id}`} className="view-icon-btn" title="View report">
                          <VisibilityIcon sx={{ fontSize: 19 }} />
                        </Link>
                        <button
                          className="fb-delete-btn"
                          onClick={() => handleDelete(fb)}
                          disabled={deletingKey === key}
                          title="Delete this feedback"
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 19 }} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && history.length > 0 && (
          <div className="pagination">
            <span className="pg-info">
              Showing {pageStart + 1} to {Math.min(pageStart + PAGE_SIZE, history.length)} of {history.length} entries
            </span>
            <div className="pg-controls">
              <button className="pg-btn pg-btn-word" onClick={() => goToPage(page - 1)} disabled={page === 1}>Previous</button>
              {pageNums.map((p, i) =>
                p === '...'
                  ? <span key={`e${i}`} className="pg-ellipsis">…</span>
                  : <button key={p} className={`pg-btn ${page === p ? 'active' : ''}`} onClick={() => goToPage(p)}>{p}</button>
              )}
              <button className="pg-btn pg-btn-word" onClick={() => goToPage(page + 1)} disabled={page === totalPages}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Feedback;